import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProducts } from "@/lib/api";
import { useCart, toLine } from "@/lib/cart";
import { useSeason } from "@/lib/holiday";
import { ProductCard } from "./ProductCard";
import { SplitText } from "@/components/fx/Motion";

const STEPS = [
  ["who", ["her", "him", "family", "me"]],
  ["love", ["glow", "face", "hair", "bath"]],
  ["budget", ["b1", "b2", "b3"]],
];
const LOVE = { glow: /\bbody oil\b|shimmer/, face: /\bface\b|\bneck\b|night cream|day cream/, hair: /\bhair\b|shampoo|split ends/, bath: /shower|body milk|oil shower/ };
const EXCLUDE = /relief|magnes|set\b/;
const BUDGET = { b1: 20, b2: 40, b3: Infinity };

const score = (p, a) => {
  const s = (p.title_en || p.title).toLowerCase();
  if (EXCLUDE.test(s) || !LOVE[a.love].test(s) || (a.love === "face" && /hair/.test(s))) return -1;
  let n = 10;
  if (a.who === "him") n += /men|man/.test(s) ? 6 : /shimmer/.test(s) ? -4 : 0;
  if (a.who === "her") n += /shimmer|face|serum/.test(s) ? 3 : /men/.test(s) ? -6 : 0;
  if (a.who === "family") n += p.brand === "Bellecare" ? 4 : 0;
  if (a.who === "me") n += p.brand === "Primus" ? 2 : 0;
  return n + (p.compare_at_price ? 1 : 0);
};

export const GiftQuiz = () => {
  const { t } = useI18n();
  const { data = [] } = useProducts({});
  const { checkout } = useCart();
  const { active, tierFor } = useSeason();
  const [ans, setAns] = useState({});
  const step = STEPS.findIndex(([k]) => !ans[k]);
  const done = step === -1;
  const picks = useMemo(() => {
    if (!done) return [];
    const pool = data.filter((p) => p.available && p.variants.length === 1 && p.price <= BUDGET[ans.budget]);
    return pool.map((p) => [score(p, ans), p]).filter(([n]) => n >= 0).sort((a, b) => b[0] - a[0]).slice(0, 3).map(([, p]) => p);
  }, [done, data, ans]);
  const tier = active ? tierFor(picks.length) : null;
  const back = () => { const k = STEPS[(done ? STEPS.length : step) - 1]?.[0]; if (k) setAns(({ [k]: _, ...rest }) => rest); };

  return (
    <section id="quiz" data-testid="gift-quiz-section" className="max-w-5xl mx-auto px-5 lg:px-10 py-24 scroll-mt-28">
      <p className="eyebrow mb-4 flex items-center gap-2"><Sparkles size={13} /> {t("quiz_eyebrow")}</p>
      <h2 className="font-display text-3xl lg:text-5xl mb-10"><SplitText text={t("quiz_title")} /></h2>
      <div className="border border-[#c9a55c]/30 bg-[#0e0d0b] p-6 lg:p-10 min-h-[320px]">
        <div className="flex items-center justify-between mb-8">
          <div className="flex gap-2">{STEPS.map(([k], i) => <span key={k} className={`h-1 w-12 transition-colors ${i < (done ? 3 : step) ? "bg-gold" : i === step ? "bg-gold/50" : "bg-white/10"}`} />)}</div>
          {(step > 0 || done) && <button data-testid="quiz-back-button" onClick={back} className="text-xs text-white/50 hover:text-white flex items-center gap-1"><ArrowLeft size={13} /> {t("quiz_back")}</button>}
        </div>
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4 }}>
              <p className="text-xs text-white/40 mb-2">{t("quiz_step", { n: step + 1 })}</p>
              <p data-testid="quiz-question" className="font-display text-2xl lg:text-3xl mb-8">{t(`quiz_q${step + 1}`)}</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {STEPS[step][1].map((o, i) => (
                  <motion.button key={o} data-testid={`quiz-option-${o}`} onClick={() => setAns({ ...ans, [STEPS[step][0]]: o })}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} whileHover={{ y: -4 }}
                    className="h-20 border border-white/15 hover:border-[#c9a55c] hover:bg-[#c9a55c]/10 transition-colors text-sm px-3">{t(`quiz_${o}`)}</motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} data-testid="quiz-result">
              <p className="font-display text-2xl lg:text-3xl mb-8">{t("quiz_result")}</p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">{picks.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {picks.length > 1 && <button data-testid="quiz-bundle-button" onClick={() => checkout(picks.map((p) => toLine(p, p.variants[0])), tier?.code)} className="btn-gold btn-shine h-14 px-8">{t("quiz_bundle")}{tier && ` · -${tier.pct}%`}</button>}
                <button data-testid="quiz-restart-button" onClick={() => setAns({})} className="btn-ghost h-14 px-8">{t("quiz_again")}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
